import { useState } from 'react';
import { backendClient } from '@/lib/backendClient';
import { useToast } from '@/hooks/use-toast';

export interface FeedbackManagementActions {
  markAsRead: (sessionId: string) => Promise<void>;
  markAsUnread: (sessionId: string) => Promise<void>;
  deleteFeedback: (sessionId: string) => Promise<void>;
  archiveFeedback: (sessionId: string) => Promise<void>;
  addPractitionerNote: (sessionId: string, note: string) => Promise<void>;
}

export const useFeedbackManagement = (): FeedbackManagementActions & { loading: boolean } => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const markAsRead = async (sessionId: string) => {
    setLoading(true);
    try {
      const { error } = await backendClient
        .from('sessions')
        .update({
          feedback_status: 'read',
          feedback_read_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feedback marked as read",
      });
    } catch (error) {
      console.error('Error marking feedback as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark feedback as read",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsUnread = async (sessionId: string) => {
    setLoading(true);
    try {
      const { error } = await backendClient
        .from('sessions')
        .update({
          feedback_status: 'unread',
          feedback_read_at: null
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feedback marked as unread",
      });
    } catch (error) {
      console.error('Error marking feedback as unread:', error);
      toast({
        title: "Error",
        description: "Failed to mark feedback as unread",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteFeedback = async (sessionId: string) => {
    setLoading(true);
    try {
      const { error } = await backendClient
        .from('sessions')
        .update({
          client_feedback: null,
          feedback_status: 'deleted',
          feedback_read_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feedback deleted successfully",
      });
    } catch (error) {
      console.error('Error deleting feedback:', error);
      toast({
        title: "Error",
        description: "Failed to delete feedback",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const archiveFeedback = async (sessionId: string) => {
    setLoading(true);
    try {
      const { error } = await backendClient
        .from('sessions')
        .update({
          feedback_status: 'archived',
          feedback_read_at: new Date().toISOString()
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Feedback archived",
      });
    } catch (error) {
      console.error('Error archiving feedback:', error);
      toast({
        title: "Error",
        description: "Failed to archive feedback",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const addPractitionerNote = async (sessionId: string, note: string) => {
    setLoading(true);
    try {
      const { error } = await backendClient
        .from('sessions')
        .update({
          practitioner_notes: note
        })
        .eq('id', sessionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Note added successfully",
      });
    } catch (error) {
      console.error('Error adding practitioner note:', error);
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    markAsRead,
    markAsUnread,
    deleteFeedback,
    archiveFeedback,
    addPractitionerNote,
    loading
  };
};
